import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, Divider, Tooltip } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import { Link, useLocation } from 'react-router-dom';
import { isDesignMode } from '../config/designMode';
import { useAuth } from '../contexts/Auth';
import { useThemeContext } from '../contexts/ThemeContext';
import { VISUAL_OPTIONS } from '../design/visualOptions';

export default function Navigation({ onLogout }) {
  const [menu, setMenu] = useState(null);
  const [account, setAccount] = useState(null);
  const { user } = useAuth();
  const { darkMode, toggleDarkMode, visualOptionId, setVisualOptionId } =
    useThemeContext();
  const { pathname } = useLocation();
  const links = [
    { name: 'Workspaces', to: '/' },
    { name: 'Templates', to: '/templates' },
    { name: 'Block catalog', to: '/agents' },
    ...(!isDesignMode() ? [{ name: 'Secrets', to: '/secrets' }] : []),
  ];
  return (
    <header className="canvas-navigation">
      <IconButton
        aria-label="Open workspace menu"
        size="small"
        onClick={(e) => setMenu(e.currentTarget)}
      >
        <MenuIcon fontSize="small" />
      </IconButton>
      <Link to="/" className="canvas-wordmark">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <rect x="3" y="3" width="6" height="6" rx="1.5" />
          <rect x="15" y="15" width="6" height="6" rx="1.5" />
          <path d="M9 6h6a3 3 0 0 1 3 3v6M6 9v6a3 3 0 0 0 3 3h6" />
        </svg>
        canvas<span>.</span>
      </Link>
      <span className="suite-label">by Container Labs</span>
      <nav className="canvas-top-links" aria-label="Canvas pages">
        {links.slice(0, 2).map((l) => (
          <Link
            key={l.to}
            to={l.to}
            aria-current={pathname === l.to ? 'page' : undefined}
          >
            {l.name}
          </Link>
        ))}
      </nav>
      <div className="navigation-spacer" />
      {isDesignMode() && <span className="preview-badge">Local preview</span>}
      <Tooltip
        title={
          darkMode ? 'Switch to light appearance' : 'Switch to dark appearance'
        }
      >
        <IconButton
          size="small"
          aria-label={
            darkMode
              ? 'Switch to light appearance'
              : 'Switch to dark appearance'
          }
          onClick={toggleDarkMode}
        >
          {darkMode ? (
            <LightModeOutlinedIcon fontSize="small" />
          ) : (
            <DarkModeOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      <IconButton
        size="small"
        aria-label="Account and appearance"
        onClick={(e) => setAccount(e.currentTarget)}
      >
        <AccountCircleOutlinedIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
        {links.map((l) => (
          <MenuItem
            key={l.to}
            component={Link}
            to={l.to}
            onClick={() => setMenu(null)}
          >
            {l.name}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem component="a" href="https://ada.containerlabs.io">
          Open Ada ↗
        </MenuItem>
        <MenuItem component="a" href="https://loft.containerlabs.io">
          Open Loft ↗
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={account}
        open={Boolean(account)}
        onClose={() => setAccount(null)}
      >
        <MenuItem disabled>{user?.email || 'Your workspace'}</MenuItem>
        <Divider />
        {VISUAL_OPTIONS.map((o) => (
          <MenuItem
            key={o.id}
            selected={o.id === visualOptionId}
            onClick={() => {
              setVisualOptionId(o.id);
              setAccount(null);
            }}
          >
            {o.name} appearance
          </MenuItem>
        ))}
        {!isDesignMode() && (
          <MenuItem
            onClick={() => {
              setAccount(null);
              onLogout();
            }}
          >
            Sign out
          </MenuItem>
        )}
      </Menu>
    </header>
  );
}
